<template>
  <teleport :disabled="!toast" to="body">
    <transition name="fade">
      <div
        v-if="display"
        class="notification-panel"
        :class="[
          classContainer,
          {
            [`notification-panel--${type}`]: type,
            'notification-panel--show-close': showClose,
            'notification-panel--toast': toast,
            'critical-component-hide': toast,
          },
        ]"
        role="none"
      >
        <p
          v-if="title"
          class="notification-panel__title"
          :class="classTitle ? classTitle : 'text-heading-m'"
          v-html="title"
        />

        <p
          class="notification-panel__text"
          :class="classText ? classText : 'text-body-m-regular'"
          v-html="text"
        />

        <span
          v-if="timeout"
          class="notification-panel__timeout"
          :style="{ animationDuration: `${timeout / 1000}s` }"
        />

        <button
          v-if="showClose"
          class="notification-panel__close icon-button"
          type="button"
          @click="closeNotification"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.close')"
          />

          <icon-close />
        </button>
      </div>
    </transition>
  </teleport>
</template>

<script>
/**
 * Vue: Notification panel (notification-panel)
 * -----------------------------------------------------------------------------
 * Temporary notification panel to indicate errors or success messages.
 * - Role = none to work with index.vue status live region, see example 4p,
 *   https://codepen.io/scottohara/pen/dyzxwyr
 *
 * @param {String} [classContainer] - Class applied to container.
 * @param {String} [classText] - Text element class, overrides text classes.
 * @param {String} [classTitle] - Title element class, overrides text classes.
 * @param {String} [displayUrl] - Used by global notification system, not for
 * local use.
 * @param {Boolean} [showClose] - Show close button.
 * @param {String} text - Text content.
 * @param {Number} [timeout] - Fades out after timeout in ms.
 * @param {String} [title] - Title content.
 * @param {Boolean} [toast] - Act as toast/pop-up.
 * @param {String} [type] - Type of notification, `error`, `success`, or `info`,
 * if no type is provided then it defaults to general.
 *
 * @emits close - Emit notification close event.
 *
 */
import IconClose from '~icons/directional-navigation/close.svg';

export default {
  name: 'NotificationPanel',

  components: {
    IconClose,
  },

  props: {
    classContainer: {
      type: String,
      default: '',
    },
    classText: {
      type: String,
      default: '',
    },
    classTitle: {
      type: String,
      default: '',
    },
    // Used by global notification system
    // eslint-disable-next-line vue/no-unused-properties
    displayUrl: {
      type: String,
      default: '',
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    text: {
      type: String,
      default: '',
      required: true,
    },
    timeout: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: '',
    },
    toast: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
  },

  emits: {
    close: () => true,
  },

  data() {
    return {
      display: false,
    };
  },

  async mounted() {
    /**
     * Delay display so transition runs.
     */
    await cnvs.Sleep(0);
    this.display = true;

    if (!this.timeout) {
      return;
    }

    await cnvs.Sleep(this.timeout);
    this.closeNotification();
  },

  methods: {
    /**
     * Close notification.
     * - Delays emitting close until notification has finished transitioning.
     * - Can't use ontransitionend as it doesn't always emit?
     */
    async closeNotification() {
      this.display = false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.$emit('close');
        return;
      }

      await cnvs.Sleep(this.$timing('normal'));
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import './notification-panel';
</style>
